/**
 * RateMath — shared, white-label pricing breakdown that shows the *math*, not just
 * the totals, so a seller/admin can see exactly how a shipping charge was built.
 *
 * Fed by the `breakdown` object every B2C quote/seller_rate already carries
 * (rate_service._price_row): base_rate, increment_rate, increment_unit_kg,
 * increments, extra_weight_kg, cod_flat, cod_pct, cod_basis, gst_pct — plus the
 * rate's zone / slab_kg. Used by the shared CourierSelect (courier selection, both
 * portals) and both order-detail pages, so the derivation reads identically
 * everywhere.
 *
 * Shows SELLER-facing numbers only (freight / cod / gst / total / rto_estimate) —
 * never our aggregator cost.
 */

const fmt = (n) =>
  `₹${Number(n ?? 0).toLocaleString("en-IN", { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;

export function RateMath({ rate, totalLabel = "Landed total", className = "" }) {
  if (!rate) return null;
  const b = rate.breakdown || {};
  const hasIncr = Number(b.increments || 0) > 0;
  // COD only applies when there's a fee or the engine recorded a basis for it.
  const codApplies = Number(rate.cod_fee || 0) > 0 || b.cod_basis != null;

  return (
    <dl className={`rounded-lg bg-slate-50 p-4 text-sm ${className}`} data-testid="rate-math">
      {/* Freight + how it was derived */}
      <div className="flex items-center justify-between">
        <dt className="text-slate-600">Freight</dt>
        <dd className="font-medium tabular-nums text-slate-700">{fmt(rate.freight)}</dd>
      </div>
      {b.base_rate != null && (
        <p className="mb-2 mt-0.5 text-xs leading-relaxed text-slate-400" data-testid="rate-math-freight">
          {fmt(b.base_rate)} base
          {rate.zone ? ` · Zone ${rate.zone}` : ""}
          {rate.slab_kg != null ? ` · ${rate.slab_kg} kg slab` : ""}
          {hasIncr && (
            <>
              {" "}+ {b.increments} × {fmt(b.increment_rate)} per {b.increment_unit_kg} kg
              {b.extra_weight_kg != null ? ` (${b.extra_weight_kg} kg over slab)` : ""}
            </>
          )}
        </p>
      )}

      {/* COD fee + which basis won (flat vs %) */}
      {codApplies && (
        <>
          <div className="flex items-center justify-between">
            <dt className="text-slate-600">COD fee</dt>
            <dd className="font-medium tabular-nums text-slate-700">{fmt(rate.cod_fee)}</dd>
          </div>
          {(b.cod_flat != null || b.cod_pct != null) && (
            <p className="mb-2 mt-0.5 text-xs leading-relaxed text-slate-400" data-testid="rate-math-cod">
              {b.cod_basis === "pct"
                ? `${b.cod_pct}% of order value${b.cod_flat != null ? ` (higher than ${fmt(b.cod_flat)} flat)` : ""}`
                : `${fmt(b.cod_flat)} flat${b.cod_pct ? ` (higher than ${b.cod_pct}% of order value)` : ""}`}
            </p>
          )}
        </>
      )}

      {/* GST + what it's charged on */}
      <div className="flex items-center justify-between">
        <dt className="text-slate-600">GST{b.gst_pct ? ` (${b.gst_pct}%)` : ""}</dt>
        <dd className="font-medium tabular-nums text-slate-700">{fmt(rate.gst)}</dd>
      </div>
      <p className="mb-2 mt-0.5 text-xs leading-relaxed text-slate-400">
        on freight{codApplies ? " + COD fee" : ""}
      </p>

      {/* Total */}
      <div className="flex items-center justify-between border-t border-slate-200 pt-2">
        <dt className="text-sm font-semibold text-slate-700">{totalLabel}</dt>
        <dd className="text-base font-bold tabular-nums text-slate-800">{fmt(rate.total)}</dd>
      </div>

      {/* RTO estimate (not added to the total — it's what a return would cost) */}
      {rate.rto_estimate != null && (
        <div className="mt-1.5 flex items-center justify-between">
          <dt className="text-xs text-slate-400">
            RTO estimate
            {Number(rate.rto_estimate) === Number(rate.freight) ? " (same as forward)" : ""}
          </dt>
          <dd className="text-xs tabular-nums text-slate-400">{fmt(rate.rto_estimate)}</dd>
        </div>
      )}
    </dl>
  );
}

export default RateMath;
